import { motion } from 'motion/react';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans flex flex-col">
      <MarketingNav />

      <main className="flex-1 pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6 leading-tight"
          >
            Privacy Policy
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-slate-500 mb-12 font-medium"
          >
            Last Updated: 02 May 2026
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8 text-lg text-slate-700 leading-relaxed font-medium"
          >
            <section>
              <p>
                LexGat ("we," "our," or "us") respects your privacy and is committed to protecting the personal data and confidential case information entrusted to us by legal professionals. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (lexgat.com) and use our software-as-a-service application (the "Service").
              </p>
              <p className="mt-4">
                Please read this Privacy Policy carefully. By accessing or using the Service, you signify that you have read, understood, and agree to our collection, storage, use, and disclosure of your personal information as described in this Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">1. Information We Collect</h2>
              <h3 className="text-xl font-bold text-slate-800 mt-6 mb-3">a. Personal Information</h3>
              <p>
                When you register for an account, we may collect personally identifiable information, such as your name, email address, phone number, Bar Council enrollment number, Chamber or Law Firm name, and billing details.
              </p>
              <h3 className="text-xl font-bold text-slate-800 mt-6 mb-3">b. Client and Case Data</h3>
              <p>
                In the course of using the Service to manage your litigation practice, you may upload, input, or generate sensitive case files, client information, court proceedings, and legal drafts ("Case Data"). We treat Case Data with the highest degree of confidentiality, acknowledging the attorney-client privilege that may apply to such information under the laws of Pakistan.
              </p>
              <h3 className="text-xl font-bold text-slate-800 mt-6 mb-3">c. Usage Data</h3>
              <p>
                We automatically collect certain information when you visit, use, or navigate the Service. This information does not reveal your specific identity but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, location, and information about how and when you use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2 mt-4 marker:text-slate-400">
                <li><strong>To Provide and Maintain the Service:</strong> Facilitating account creation, authentication, logging, and general platform administration.</li>
                <li><strong>To Improve the Service:</strong> Analyzing usage statistics to enhance the user experience and develop new features tailored for Pakistani litigation workflows.</li>
                <li><strong>To Power AI Features:</strong> Processing Case Data strictly to provide you with intelligent drafting and analytical insights. Our AI models process your inputs contextually to generate relevant outputs.</li>
                <li><strong>To Communicate with You:</strong> Sending administrative information, security alerts, product updates, and responding to customer service inquiries.</li>
                <li><strong>To Process Payments:</strong> Managing subscriptions and processing financial transactions securely through authorized third-party payment processors.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">3. AI Processing and Confidentiality</h2>
              <p>
                LexGat integrates Artificial Intelligence (AI) to assist with case file analysis and legal drafting. When you use these AI features:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4 marker:text-slate-400">
                <li>Your Case Data is transmitted securely to our AI providers solely for the purpose of fulfilling your specific request (e.g., generating a draft or extracting case details).</li>
                <li>We explicitly prohibit our AI providers from using your Case Data, prompts, or generated outputs to train their foundational models.</li>
                <li>Your Case Data remains isolated within your workspace environment and is never commingled with or exposed to other LexGat users.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">4. Disclosure of Your Information</h2>
              <p>We do not sell your personal information or Case Data. We may share information only in the following specific circumstances:</p>
              <ul className="list-disc pl-6 space-y-2 mt-4 marker:text-slate-400">
                <li><strong>Service Providers:</strong> We may share data with trusted third-party vendors (e.g., cloud hosting, database management) who perform services on our behalf. These third parties are bound by strict contractual confidentiality and data security obligations.</li>
                <li><strong>Law Firm Members:</strong> If you are part of a Law Firm workspace, your Case Data may be visible to other authorized members of your firm, subject to the access controls defined by your workspace administrator.</li>
                <li><strong>Legal Obligations:</strong> We may disclose information if required to do so by law, or in response to a valid legal request by public authorities, recognizing and asserting attorney-client privilege constraints to the maximum extent permitted by law.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">5. Data Security</h2>
              <p>
                We implement industry-standard technical and organizational security measures, including encryption in transit and at rest, designed to protect the security of any personal information and Case Data we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">6. Data Retention</h2>
              <p>
                We will only retain your personal information and Case Data for as long as it is necessary for the purposes set out in this Privacy Policy, unless a longer retention period is required or permitted by law. Upon account termination or your explicit request, we will delete or anonymize your data securely.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">7. Changes to This Privacy Policy</h2>
              <p>
                We may update our Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top. We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">8. Contact Us</h2>
              <p>
                If you have questions or comments about this Privacy Policy, or if you believe your data privacy rights have been violated, please contact our Data Protection Officer at:
              </p>
              <p className="mt-4 font-bold text-blue-600">
                privacy@lexgat.com
              </p>
            </section>

          </motion.div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
